"use client";

import {
  cubicBezier,
  useScroll,
  useSpring,
  useTransform,
  type MotionValue,
} from "framer-motion";
import type React from "react";

import { EASE_OUT, PARALLAX_SPRING } from "./motionTiming";

/**
 * Reveals across the Bastian Keller sections are scrubbed off scroll position
 * rather than played on a timer.
 *
 * A timed reveal fires when an element's edge touches the viewport and then runs
 * to completion whether or not you are still looking — on a tall section it is
 * finished before the content is properly on screen. Driving the same reveal from
 * scroll position means an element arrives as you travel past it, holds wherever
 * you stop, and retreats if you scroll back.
 */

/** Shared easing, as a function rather than the bezier tuple `transition` takes. */
export const REVEAL_EASE = cubicBezier(
  EASE_OUT[0],
  EASE_OUT[1],
  EASE_OUT[2],
  EASE_OUT[3],
);

/**
 * Fraction of the range each block's reveal occupies. Deliberately wide: blocks
 * are handed equal starting points, so a window this large leaves four or five of
 * them mid-reveal at any scroll position, which is what makes a group read as one
 * stacked move instead of a queue taking turns.
 */
export const REVEAL_WINDOW = 0.45;

export const REVEAL_RISE = "2.5rem";

/**
 * Default range for a single block: from its top edge 85% down the screen to 45%
 * down. Both ends are anchored to the same edge, so the scroll it spans is a
 * fixed fraction of the viewport regardless of how tall the block is.
 *
 * It starts at 0.85 rather than at the viewport edge because content usually sits
 * behind some padding — anchoring to the edge begins the reveal while the thing
 * being revealed is still below the fold.
 */
export const REVEAL_OFFSET = ["start 0.85", "start 0.45"];

/**
 * Range for a section whose blocks are spread down its whole height: from its top
 * edge 80% down the screen to its bottom edge at the same line, so the scroll it
 * spans is the section's own height and the last block lands as it arrives.
 */
export const SPAN_OFFSET = ["start 0.8", "end 0.8"];

/**
 * Range for a drift that runs an element's whole crossing of the viewport, from
 * its top edge entering the bottom to its bottom edge leaving the top. Shared by
 * the gallery scatter and the timeline entries, which both separate two columns
 * for as long as they are on screen rather than over a reveal window.
 */
export const CROSSING_OFFSET = ["start end", "end start"];

/**
 * Progress across a block's own scroll range, smoothed so it trails the wheel.
 *
 * `still` is the off switch, and it withholds the target rather than throwing the
 * result away. That distinction is the whole point: framer resolves a target's
 * range by walking its offsetParent chain — twice, once to measure and once to
 * resolve the offsets — for every tracked element on every scroll frame. With no
 * target it reads a couple of properties off the document instead, and this page
 * tracks around twenty-eight elements.
 *
 * The value it then produces is the document's own progress and means nothing
 * here, which is safe only because it is never read: every consumer of a `still`
 * progress maps it to a constant, either through `useReveal`'s `reduceMotion` or
 * through a drift whose travel is zero. Note the spring is left attached to the
 * same source in both modes — framer's `useSpring` subscribes to whichever value
 * it saw first and does not re-subscribe, so swapping the source underneath it
 * would quietly do nothing.
 */
export function useRevealProgress(
  ref: React.RefObject<HTMLElement | null>,
  offset: string[] = REVEAL_OFFSET,
  still: boolean = false,
) {
  const { scrollYProgress } = useScroll({
    target: still ? undefined : (ref as React.RefObject<HTMLElement>),
    offset: offset as never,
  });

  return useSpring(scrollYProgress, PARALLAX_SPRING);
}

/** Where block `index` of `total` starts within the shared range. */
export const revealStart = (index: number, total: number) =>
  total <= 1 ? 0 : (index / (total - 1)) * (1 - REVEAL_WINDOW);

/**
 * Maps one block's slice of a range onto a 0-1 reveal, plus the rise that goes
 * with it. `reveal` is the value to hang everything else off — opacity, a clip
 * inset, a counter — so a block's parts always move together.
 */
export function useReveal(
  progress: MotionValue<number>,
  index: number = 0,
  total: number = 1,
  reduceMotion: boolean = false,
) {
  const start = revealStart(index, total);
  const reveal = useTransform(
    progress,
    [start, start + REVEAL_WINDOW],
    reduceMotion ? [1, 1] : [0, 1],
    { ease: REVEAL_EASE },
  );
  const y = useTransform(reveal, [0, 1], [REVEAL_RISE, "0rem"]);

  /**
   * The same slice, un-eased.
   *
   * REVEAL_EASE is heavily front-loaded — a value riding `reveal` is 76% of the
   * way through by the first quarter of the window. That is exactly what a fade
   * or a rise wants, and exactly wrong for anything you are meant to watch
   * progress, like a figure counting up: it races to almost its final value and
   * then crawls. Linear spends the window evenly instead.
   */
  const linear = useTransform(
    progress,
    [start, start + REVEAL_WINDOW],
    reduceMotion ? [1, 1] : [0, 1],
  );

  return { reveal, y, linear };
}
